/* eslint-disable tsdoc/syntax */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useEffect, useState } from "react";
import { useNavigationType } from "react-router-dom";

import {
  useMobileVerificationContext,
  useNotificationContext,
  usePersonalDetailsContext,
  useSignUpContext,
} from "@zenith/context";
import { AppNavigation, AppStepper } from "@zenith/hooks";
import {
  ButtonComponent,
  PinInputComponent,
  PinRulesComponent,
  SelectComponent,
  TermsAndConditionsComponent,
  TextFieldComponent,
  ToastSnackbarComponent,
} from "@zenith/components";
import {
  ApiResponseDTOListCountrySummarizedDTO,
  CountrySummarizedDTO,
  CreateUserOnboardingRequestDTO,
  OnboardingApiService,
  ResourceApiService,
  UtilityApiService,
  ValidationFieldRequestDTO,
} from "@zenith/services/user";
import { SelectItemsType } from "@zenith/components/ui/SelectComponent/select-component/interfaces";
import {
  BENEFICIARY_EMAIL_OR_NUMBER_ALREADY_REGISTERED,
  NO_BENEFICIARY_WAS_FOUND_ERROR_CODE,
  CLIENT_ERROR_BAD_REQUEST,
  PIN_LENGTH,
  REPEATED_REGEX_SIGN_UP,
  SEQUENTIAL_REGEX_SIGN_UP,
  TERMS_AND_CONDITIONS,
  PHONE_NUMBER_IS_INVALID,
  NUMBER_INPUT_REGEX,
  DEFAULT_COUNTRY_CODE,
  DEFAULT_CHANNEL,
} from "@zenith/data/constants";
import {
  countryCodeToEnum,
  encrypt,
  getApiErrorMessage,
  getCountryFlag,
} from "@zenith/utils";
import {
  setConfirmPinSignUpValidations,
  setPhoneNumberValidations,
  setPinValidations,
} from "@zenith/features/Authentication/validations/AuthValidations";
import { getPhoneNumberLength } from "@zenith/features/globalUtils";
import { SIGN_IN, SIGN_UP_MOBILE_VERIFICATION } from "@zenith/routes/paths";
import { SignUpDataType, SignUpErrorsTypes } from "./interfaces";
import WalletIcon from "@zenith/assets/icons/WalletIcon";
import { Logo } from "@zenith/shared";

/* eslint-disable-next-line */
export interface SignUpPageProps {}

const SignUpPage = memo(function SignUpPage(props: SignUpPageProps) {
  const xApiKey = import.meta.env.VITE_X_API_KEY;

  const { setActiveStep } = AppStepper();
  const { setPhoneNumber, setCountryCode } = useMobileVerificationContext();
  const { clearPersonalDetailsContext } = usePersonalDetailsContext();
  const { setSignUpContext } = useSignUpContext();
  const navType = useNavigationType();
  const { navigateTo } = AppNavigation();

  useEffect(() => {
    if (navType === "POP" || navType === "PUSH") {
      setPhoneNumber("");
      setCountryCode("");
      clearPersonalDetailsContext();
      setActiveStep(0);
    }
  }, [setPhoneNumber, setCountryCode, setActiveStep, clearPersonalDetailsContext, navType]);

  const [countries, setCountries] = useState<SelectItemsType[]>([]),
    [isCountriesLoading, setIsCountriesLoading] = useState<boolean>(false),
    [createPin, setCreatePin] = useState<Array<number | string>>(
      new Array(PIN_LENGTH).fill("")
    ),
    [confirmPin, setConfirmPin] = useState<Array<number | string>>(
      new Array(PIN_LENGTH).fill("")
    ),
    [isSequential, setIsSequential] = useState(true),
    [isRepeated, setIsRepeated] = useState(true),
    initialFormErrorState = {
      countryCode: {
        message: "",
        hasError: false,
      },
      phoneNumber: {
        message: "",
        hasError: false,
      },
      createPin: {
        message: "",
        hasError: false,
      },
      confirmPin: {
        message: "",
        hasError: false,
      },
    },
    [formErrors, setFormErrors] = useState<SignUpErrorsTypes>(
      initialFormErrorState
    ),
    [formData, setFormData] = useState<SignUpDataType>({
      countryCode: DEFAULT_COUNTRY_CODE,
      phoneNumber: "",
      createPin: "",
      confirmPin: "",
    });
  const [isLoading, setIsLoading] = useState(false);
  const [isExistingAccountLoading, setIsExistingAccountLoading] =
    useState(false);

  const [toasterData, setToasterData] = useState<{
    title: string;
    message: string;
    status: "success" | "error" | "warning";
  }>({
    title: "",
    message: "",
    status: "success",
  });

  const { setOpen } = useNotificationContext();

  const [isChecked, setIsChecked] = useState(false);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked);
  };

  function notify(
    title: string,
    message: string,
    status: "success" | "error" | "warning"
  ): void {
    setToasterData({
      title,
      message,
      status,
    });
    setOpen(true);
  }

  useEffect(() => {
    setIsCountriesLoading(true);

    ResourceApiService.getCountries()
      .then((res: ApiResponseDTOListCountrySummarizedDTO) => {
        if (!res.data) return;
        const items = res.data.map((country: CountrySummarizedDTO) => ({
          value: country.countryCode as string,
          label: country.countryCallingCode as string,
          icon: getCountryFlag(country.countryCode as string),
          countryName: country.countryName,
        }));

        setCountries(items);
      })
      .catch((error) => {
        return error;
      })
      .finally(() => setIsCountriesLoading(false));
  }, []);

  function onCountryCodeChange(
    event: React.SyntheticEvent | null,
    newValue: string | null
  ): void {
    if (event && newValue)
      setFormData({
        ...formData,
        countryCode: newValue as string,
        phoneNumber: "",
      });
  }

  function onPhoneNumberChange(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    setFormErrors({
      ...formErrors,
      phoneNumber: {
        message: "",
        hasError: false,
      },
    });
    setFormData({
      ...formData,
      phoneNumber: event.target.value.replace(NUMBER_INPUT_REGEX, "") as string,
    });
  }

  function onPhoneNumberBlur(): void {
    setFormErrors({
      ...formErrors,
      phoneNumber: {
        ...setPhoneNumberValidations(
          formData.phoneNumber,
          getPhoneNumberLength(formData.countryCode, formData.phoneNumber)
        ),
      },
    });
  }

  function onCreatePin(pinEntry: number | string, index: number): void {
    const pin = [...createPin];
    pin[index] = pinEntry;
    setCreatePin(pin);
    setFormData({ ...formData, createPin: pin.join("") });
    setFormErrors({
      ...formErrors,
      createPin: {
        message: "",
        hasError: false,
      },
    });
    updatePinState(pin.join(""));
  }

  function onCreatePinBlur(): void {
    setFormErrors({
      ...formErrors,
      createPin: {
        ...setPinValidations(formData.createPin),
      },
    });
    if (formData.confirmPin) {
      setFormErrors({
        ...formErrors,
        confirmPin: {
          ...setConfirmPinSignUpValidations(
            formData.confirmPin,
            formData.createPin
          ),
        },
        createPin: {
          ...setPinValidations(formData.createPin),
        },
      });
    }
  }

  function onConfirmPin(pinEntry: number | string, index: number): void {
    const confirmationPin = [...confirmPin];
    confirmationPin[index] = pinEntry;
    setConfirmPin(confirmationPin);
    setFormData({ ...formData, confirmPin: confirmationPin.join("") });
    setFormErrors({
      ...formErrors,
      confirmPin: {
        message: "",
        hasError: false,
      },
    });
  }

  function onConfirmPinBlur(): void {
    setFormErrors({
      ...formErrors,
      confirmPin: {
        ...setConfirmPinSignUpValidations(
          formData.confirmPin,
          formData.createPin
        ),
      },
    });
  }

  function updatePinState(createPin: string): void {
    if (createPin.length === PIN_LENGTH) {
      const sequential = SEQUENTIAL_REGEX_SIGN_UP.test(createPin);
      const repeated = REPEATED_REGEX_SIGN_UP.test(createPin);
      setIsSequential(sequential);
      setIsRepeated(repeated);
    } else {
      setIsSequential(true);
      setIsRepeated(true);
    }
  }

  function mapCountryCodeToPhoneNumber(): string {
    switch (formData.countryCode) {
      case "NG":
        return "+234" + formData.phoneNumber;
      case "GH":
        return "+233" + formData.phoneNumber;
      default:
        return "";
    }
  }

  function invalidFormData(formData: SignUpDataType): boolean {
    return (
      !formData.countryCode ||
      !formData.phoneNumber ||
      formData.createPin.length !== PIN_LENGTH ||
      formData.confirmPin.length !== PIN_LENGTH ||
      formData.createPin !== formData.confirmPin ||
      SEQUENTIAL_REGEX_SIGN_UP.test(formData.createPin) ||
      REPEATED_REGEX_SIGN_UP.test(formData.createPin)
    );
  }

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault();
    setIsLoading(true);

    setFormErrors({
      ...formErrors,
      phoneNumber: {
        ...setPhoneNumberValidations(
          formData.phoneNumber,
          getPhoneNumberLength(formData.countryCode, formData.phoneNumber)
        ),
      },
      createPin: {
        ...setPinValidations(formData.createPin),
      },
      confirmPin: {
        ...setConfirmPinSignUpValidations(
          formData.confirmPin,
          formData.createPin
        ),
      },
    });

    if (
      formErrors.phoneNumber.hasError ||
      formErrors.confirmPin.hasError ||
      formErrors.createPin.hasError
    ) {
      setIsLoading(false);
      return;
    }

    try {
      await createNewUserHandler(
        false,
        CreateUserOnboardingRequestDTO.purpose.NON_CUSTOMER_SIGN_UP
      );
    } catch (error: any) {
      if (error.body?.internalCode === "ZENZ-CROSS-008") {
        notify("Onboarding", error.body.message, "error");
      } else if (error.body?.internalCode === CLIENT_ERROR_BAD_REQUEST) {
        notify("Onboarding", error.body.message, "error");
        setFormErrors({
          ...formErrors,
          phoneNumber: {
            message: error.body.message,
            hasError: true,
          },
        });
      } else if (
        error.body?.internalCode === NO_BENEFICIARY_WAS_FOUND_ERROR_CODE ||
        error.body?.internalCode ===
          BENEFICIARY_EMAIL_OR_NUMBER_ALREADY_REGISTERED
      ) {
        notify(
          "Onboarding",
          "This phone number is already registered.",
          "error"
        );
        setFormErrors({
          ...formErrors,
          phoneNumber: {
            message: "This phone number is already registered.",
            hasError: true,
          },
        });
      } else {
        notify("Onboarding", getApiErrorMessage(error), "error");
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleExistingAccountSubmit(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): Promise<void> {
    event.preventDefault();
    if (formErrors.phoneNumber.hasError) return;
    setIsExistingAccountLoading(true);
    try {
      const validateFieldRequest: ValidationFieldRequestDTO = {
        fieldName: ValidationFieldRequestDTO.fieldName.PHONE_NUMBER,
        fieldValue: mapCountryCodeToPhoneNumber(),
      };

      const validateFieldResponse = await UtilityApiService.validateField(
        validateFieldRequest
      );

      if (validateFieldResponse.success && validateFieldResponse.data) {
        const validateFieldData = validateFieldResponse.data;
        if (validateFieldData.isValid) {
          setPhoneNumber(mapCountryCodeToPhoneNumber());
          setCountryCode(formData.countryCode);
          await createNewUserHandler(
            true,
            CreateUserOnboardingRequestDTO.purpose.CUSTOMER_SIGN_UP
          );
        } else {
          setFormErrors({
            ...formErrors,
            phoneNumber: {
              hasError: true,
              message: PHONE_NUMBER_IS_INVALID,
            },
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsExistingAccountLoading(false);
    }
  }

  async function createNewUserHandler(
    newUser: boolean,
    purpose: CreateUserOnboardingRequestDTO.purpose
  ): Promise<void> {
    const responsePublicKey = await UtilityApiService.getPublicKey(
      DEFAULT_CHANNEL,
      xApiKey
    );
    const publicKeyBase64 = responsePublicKey.data?.publicKeyBase64 || "";
    const encryptedCreatePin = encrypt(formData.createPin, publicKeyBase64);
    const encryptedConfirmPin = encrypt(formData.confirmPin, publicKeyBase64);

    const requestBodyNewUser: CreateUserOnboardingRequestDTO = {
      confirmPasscode: encryptedConfirmPin,
      countryCode: formData.countryCode,
      passcode: encryptedCreatePin,
      phoneNumber: mapCountryCodeToPhoneNumber(),
      purpose: purpose,
    };

    const responseNewUser = await OnboardingApiService.createNewUserOnboarding(
      requestBodyNewUser
    );
    if (responseNewUser.success) {
      setPhoneNumber(mapCountryCodeToPhoneNumber());
      setCountryCode(countryCodeToEnum(formData.countryCode));
      if (encryptedCreatePin) {
        setSignUpContext({
          countryCode: formData.countryCode,
          encryptedPin: encryptedCreatePin,
          phoneNumber: formData.phoneNumber,
        });
      }
      navigateTo(SIGN_UP_MOBILE_VERIFICATION);
    } else {
      notify("Onboarding", getApiErrorMessage(responseNewUser), "error");
    }
  }

  function handleSignIn(): void {
    navigateTo(SIGN_IN);
  }

  return (
    <div className="my-2 min-h-screen w-full overflow-x-hidden">
      {toasterData.title && toasterData.message && (
        <ToastSnackbarComponent
          horizontal="center"
          status={toasterData.status}
          vertical="top"
          hasAutoHideDuration
          size="lg"
          title={toasterData.title}
          text={toasterData.message}
        />
      )}

      <div className="my-12 mx-auto flex w-full flex-1 flex-col items-center justify-center md:w-[85%]">
        <div className="absolute top-8 flex gap-2 lg:hidden">
          <WalletIcon height={40} width={40} className="my-auto" />
          <Logo />
        </div>
        <div className="my-3 mt-14 flex w-full flex-col items-center justify-center lg:mt-0">
          <h5 className="w-full text-center text-2xl font-medium">
            Transform Your Financial Future
          </h5>
          <span className=" text-neutral-600">
            Open a Finopay account in 5 minutes!
          </span>
        </div>
        <div className="w-full">
          <form
            onSubmit={handleSubmit}
            className="mx-auto w-full max-w-md sm:w-5/6 md:w-2/3 lg:w-5/6 xl:w-2/3"
            noValidate
          >
            <div className="flex w-full flex-row gap-x-1 md:gap-x-4 md:px-0">
              <SelectComponent
                id="country-code-dropdown"
                label="Country"
                labelId="country-code-dropdown-label"
                className=""
                defaultValue={formData.countryCode}
                onChange={onCountryCodeChange}
                items={countries}
                labelClassName="text-primary-300"
                isLoading={isCountriesLoading}
                editable={false}
              />
              <div className="flex w-full grow flex-col">
                <TextFieldComponent
                  id="phone-number-field"
                  labelId="phone-number-field-label"
                  className="w-5/6 xsm:w-full"
                  containerClassName="flex grow"
                  hasError={formErrors.phoneNumber.hasError}
                  label="Phone number"
                  placeholder="Enter phone number"
                  maxLength={getPhoneNumberLength(
                    formData.countryCode,
                    formData.phoneNumber
                  )}
                  minLength={getPhoneNumberLength(
                    formData.countryCode,
                    formData.phoneNumber
                  )}
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={onPhoneNumberChange}
                  onBlur={onPhoneNumberBlur}
                />
                {formErrors.phoneNumber.hasError && (
                  <div className="mt-1 ml-2 text-xs font-normal text-primary-250">
                    <span className="break-words">
                      {formErrors.phoneNumber.message}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-8 w-full md:px-0">
              <PinInputComponent
                id="sign-up-pin-create"
                label="Create six (6) digit Passcode"
                labelClassName="ml-2 mb-1 text-neutral-500 text-sm font-normal"
                pinLength={PIN_LENGTH}
                pin={createPin}
                pinContainerClass="gap-2 md:gap-4 xl:gap-6"
                masked={true}
                type={"tel"}
                onPinChanged={onCreatePin}
                onBlur={onCreatePinBlur}
                hasError={formErrors.createPin.hasError}
                isValidating={false}
                validationMessage={formErrors.createPin.message}
                validationClassName="font-normal text-xs"
              />
            </div>

            <div className=" w-full">
              <PinInputComponent
                id="sign-up-pin-confirm"
                label="Confirm six (6) digit Passcode"
                labelClassName="ml-2 mb-1 text-neutral-500 text-sm font-normal"
                pinLength={PIN_LENGTH}
                pin={confirmPin}
                masked={true}
                type={"tel"}
                pinContainerClass="gap-2 md:gap-4 xl:gap-6"
                onPinChanged={onConfirmPin}
                onBlur={onConfirmPinBlur}
                hasError={formErrors.confirmPin.hasError}
                isValidating={false}
                validationMessage={formErrors.confirmPin.message}
                validationClassName="font-normal text-xs"
              />
            </div>
            <div className="">
              <PinRulesComponent
                className=""
                isRepeated={isRepeated}
                isSequential={isSequential}
                necessaryPinLength={PIN_LENGTH}
                pinLength={formData.createPin.length}
              />
            </div>

            <div className="mt-4 w-full">
              <TermsAndConditionsComponent
                text="checkbox"
                to={TERMS_AND_CONDITIONS}
                onCheckboxChange={handleCheckboxChange}
                isChecked={isChecked}
              />
            </div>
            <div className="mt-8 flex flex-col items-center">
              {/* <ButtonComponent
                label="An existing Zenith account"
                fullWidth
                onClick={handleExistingAccountSubmit}
                disabled={
                  invalidFormData(formData) ||
                  !isChecked ||
                  isExistingAccountLoading
                }
                isLoading={isExistingAccountLoading}
                size="large"
                type="secondary"
                id="sign-up-button"
                iconStart={<ZenithIcon />}
                className="mb-4"
              /> */}

              <ButtonComponent
                label="Get Started"
                fullWidth
                onClick={handleSubmit}
                disabled={invalidFormData(formData) || isLoading || !isChecked}
                isLoading={isLoading}
                size="large"
                type="primary"
                id="sign-up-button"
              />

              <div className="mt-8 flex gap-1 text-base font-normal leading-normal text-neutral-500">
                Have an account?{" "}
                <ButtonComponent
                  label="Sign In"
                  onClick={handleSignIn}
                  id="sign-up"
                  size="large"
                  type="tertiary"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
});

export default SignUpPage;
